import { useAuth } from './AuthContext';
import AuthFlowContainer from '../user/AuthFlowContainer';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { logClient404Hit } from '../api/client404Api';
import { parseCollectionTablePath } from '../utils/collectionPaths';

export const PrivateRoute = ({
  requireAdmin,
  children,
  logClient404WhenUnauthenticated,
}: {
  requireAdmin?: boolean;
  children: any;
  /** When true, record a client 404 hit (public) if the user is a guest on a collection-style URL. */
  logClient404WhenUnauthenticated?: boolean;
}) => {
  const { user, loading, authenticated, refreshUser } = useAuth();
  const location = useLocation();
  const [showAuthenticating, setShowAuthenticating] = useState(true);

  // Re-validate token when landing on root URL (reload or navigation)
  useEffect(() => {
    if (location.pathname === '/') {
      refreshUser();
    }
  }, [location.pathname, refreshUser]);

  // Only show "Authenticating..." for a brief moment
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowAuthenticating(false);
    }, 1000); // Show for max 1 second

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (
      loading ||
      authenticated ||
      !logClient404WhenUnauthenticated ||
      typeof document === 'undefined'
    ) {
      return;
    }
    if (!parseCollectionTablePath(location.pathname)) {
      return;
    }
    const path = `${location.pathname}${location.search || ''}`;
    logClient404Hit({
      path: path.startsWith('/') ? path : `/${path}`,
      kind: 'unmatched_route',
      referrer: document.referrer || undefined,
    });
  }, [loading, authenticated, logClient404WhenUnauthenticated, location.pathname, location.search]);

  if (loading && showAuthenticating) {
    return <>Authenticating...</>;
  }

  // Check both top-level admin and nested info.admin
  const isAdmin = user?.admin || user?.info?.admin;
  if (requireAdmin && !isAdmin) {
    return <div>Access denied. Admin privileges required.</div>;
  }

  if (authenticated && user) {
    return <>{children}</>;
  } else {
    return <AuthFlowContainer />;
  }
};